import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import type { AgentActionDTO, CursorPage } from "@ai-crm/shared";
import { Button } from "./ui/button";
export function agentActionLabel(action: AgentActionDTO): string {
  if (
    action.effects.some(
      (e) =>
        e.type === "PATCH_FIELDS" && ["UNKNOWN", "IN_FLIGHT"].includes(e.state),
    )
  )
    return "Исход обновления CRM неизвестен";
  if (action.kind === "CRM_PATCH" && action.state !== "DONE")
    return "Данные CRM требуют проверки";
  if (action.state === "DONE") return "Завершено";
  if (action.state === "CANCELLED") return "Отменено";
  if (
    action.effects.some(
      (e) => e.type === "MOVE_STAGE" && e.state === "CONFIRMED",
    )
  )
    return "Передано; заметка ожидает записи";
  if (action.state === "UNKNOWN") return "Проверяем результат";
  if (action.safeErrorCode === "PERMISSION_DENIED") return "Нет прав";
  return "Передача ожидается";
}
export interface AgentActionsPanelProps {
  funnelId: string;
  load: (id: string, cursor?: string) => Promise<CursorPage<AgentActionDTO>>;
  reconcile: (id: string) => Promise<AgentActionDTO>;
  scope?: string;
}
export function AgentActionsPanel({
  funnelId,
  load,
  reconcile,
  scope = "account",
}: AgentActionsPanelProps) {
  const [cursor, setCursor] = useState<string | undefined>();
  const query = useQuery({
    queryKey: ["agent-actions", scope, funnelId, cursor],
    queryFn: () => load(funnelId, cursor),
    refetchInterval: 5000,
    refetchOnWindowFocus: "always",
  });
  const mutation = useMutation({
    mutationFn: reconcile,
    onSuccess: () => {
      void query.refetch();
    },
  });
  return (
    <section
      className="space-y-4 rounded-lg border bg-card p-4"
      aria-label="Действия в реальных диалогах"
    >
      <h3 className="font-medium">Действия в реальных диалогах</h3>
      <p className="text-sm text-muted-foreground">Работа агента со сделками в CRM. Тестовый чат не создаёт эти действия.</p>
      {query.isPending && <p>Загрузка действий…</p>}
      {query.isError && (
        <p role="alert">
          Не удалось загрузить действия.{" "}
          <Button variant="outline" size="sm" type="button" onClick={() => void query.refetch()}>
            Обновить
          </Button>
        </p>
      )}
      {query.data?.items.length === 0 && <p>Действий пока нет.</p>}
      {query.data?.items.map((action) => (
        <article key={action.id} className="space-y-2 rounded-md border bg-background p-3">
          <p>{agentActionLabel(action)}</p>
          <p className="text-xs text-muted-foreground">
            Сделка {action.amoLeadId} ·{" "}
            {new Date(action.createdAt).toLocaleString("ru-RU")}
          </p>
          {action.effects
            .filter((e) => e.type === "PATCH_FIELDS")
            .map((e) => (
              <p key={e.ordinal}>
                {e.entity === "CONTACT" ? "Контакт" : "Сделка"}:{" "}
                {e.state === "CONFIRMED"
                  ? "Обновлено"
                  : ["UNKNOWN", "IN_FLIGHT"].includes(e.state)
                    ? "Исход неизвестен"
                    : e.reason === "AMBIGUOUS_CONTACT"
                      ? "Контакт неоднозначен"
                      : e.reason === "STALE_VALUE"
                        ? "Изменено менеджером"
                        : "Недостаточно данных"}
                {e.fieldKeys?.length ? ` · ${e.fieldKeys.join(", ")}` : ""}
              </p>
            ))}
          {action.safeErrorCode && (
            <p className="text-sm">
              {(
                {
                  PERMISSION_DENIED: "Нет прав amoCRM на запись",
                  ACCOUNT_SUSPENDED: "Аккаунт приостановлен",
                  STALE_INPUT: "Настройки или переписка изменились",
                  MOVE_CAUSALITY_UNPROVEN:
                    "Результат перевода требует проверки",
                  OUTBOUND_RECONCILIATION_REQUIRED:
                    "Ожидается подтверждение отправки",
                } as Record<string, string>
              )[action.safeErrorCode] ?? "Действие требует проверки"}
            </p>
          )}
          {["UNKNOWN", "BLOCKED", "RETRY_WAIT"].includes(action.state) && (
            <Button
              variant="outline"
              size="sm"
              type="button"
              disabled={mutation.isPending}
              onClick={() => mutation.mutate(action.id)}
            >
              Проверить результат
            </Button>
          )}
        </article>
      ))}
      {mutation.isError && (
        <p role="alert">
          Не удалось проверить результат. Повторите проверку позже.
        </p>
      )}
      <div className="flex flex-wrap gap-2">
      {query.data?.nextCursor && (
        <Button
          variant="outline"
          size="sm"
          type="button"
          onClick={() => setCursor(query.data!.nextCursor!)}
        >
          Следующие действия
        </Button>
      )}
      {cursor && (
        <Button variant="outline" size="sm" type="button" onClick={() => setCursor(undefined)}>
          К началу
        </Button>
      )}
      </div>
    </section>
  );
}
